import React, { useEffect, useRef, useImperativeHandle } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { fetchKitchenSink } from 'actions/kitchenSink';
import { fetchGeoInfo } from 'actions/geoInfo';
import { fetchRotatingSegmentTitles } from 'actions/rotatingSegmentTitles';
import { fetchDiff } from 'actions/diff';
import { useDiffInterval } from 'hooks/diff';
import useInterval from 'hooks/interval';
import { fetchScoring } from 'actions/scoring';
import { AppDispatch } from 'app-types';
import { VideoCtvRedux } from 'store-types';

interface KitchenSinkFetcherProps {
  onKSStartupError?: () => void;
}

export interface KitchenSinkFetcherHandle {
  retry: () => Promise<unknown>;
}

const KitchenSinkFetcher = React.forwardRef<
  KitchenSinkFetcherHandle,
  KitchenSinkFetcherProps
>(({ onKSStartupError = () => {} }, ref) => {
  const dispatch = useDispatch<AppDispatch>();
  const history = useHistory();

  const onKSStartupErrorRef = useRef(onKSStartupError);
  useEffect(() => {
    onKSStartupErrorRef.current = onKSStartupError;
  });
  useEffect(() => {
    const fetchData = async () => {
      const results = await dispatch(fetchKitchenSink());
      if (!results) {
        onKSStartupErrorRef.current();
      }

      await dispatch(fetchScoring());
      await dispatch(fetchGeoInfo());

      if (
        (results as { config?: { modules?: { SegmentTitles?: { enabled?: boolean } } } })
          ?.config?.modules?.SegmentTitles?.enabled
      ) {
        await dispatch(fetchRotatingSegmentTitles());
      }
    };
    fetchData();
  }, [dispatch, history]);

  const contentVersion = useSelector(
    (state: VideoCtvRedux) => state.kitchenSink.data.version,
  );
  const menuId = useSelector(
    (state: VideoCtvRedux) => state.kitchenSink.data.menuId,
  );
  const frequencyRST = useSelector(
    (state: VideoCtvRedux) => state.rotatingSegmentTitles.data.frequency,
  );
  const rotatingSegmentIds = useSelector(
    (state: VideoCtvRedux) =>
      state.updateRotatingSegmentTitles?.rotatingSegmentIds ?? [],
  );

  const segmentTitlesEnabled = useSelector(
    (state: VideoCtvRedux) =>
      state.kitchenSink.data?.config?.modules?.SegmentTitles?.enabled,
  );

  const fetchDiffInterval = useDiffInterval();
  useInterval(() => {
    if (contentVersion && menuId) {
      dispatch(fetchDiff(String(menuId)));
      if (segmentTitlesEnabled) {
        dispatch(fetchRotatingSegmentTitles());
      }
    }
  }, fetchDiffInterval);

  useInterval(() => {
    if (segmentTitlesEnabled && rotatingSegmentIds.length > 0) {
      dispatch(fetchRotatingSegmentTitles());
    }
  }, frequencyRST * 1000);

  useImperativeHandle(ref, () => ({
    retry: async () => {
      return await dispatch(fetchKitchenSink());
    },
  }));

  return null;
});

KitchenSinkFetcher.displayName = 'KitchenSinkFetcher';

export default KitchenSinkFetcher;